import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Grid,
  Chip,
  Stack,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  LinearProgress,
  Select,
  FormControl,
  InputLabel,
  MenuItem
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  RocketLaunch as LaunchIcon,
  Timeline as TimelineIcon,
  Shield as ShieldIcon,
  AutoGraph as ObjectiveIcon,
  FolderOpen as LoadIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Assignment as TaskIcon,
  PictureAsPdf as PdfIcon,
  Share as ShareIcon,
  Info as InfoIcon,
  ArrowForward as ArrowIcon,
  Visibility as VisionIcon,
  Analytics as AnalyticsIcon,
  People as ResourceIcon,
  Gavel as GovernanceIcon,
  Dashboard as PerformanceIcon,
  AutoAwesome as AdaptationIcon,
  Flag as FlagIcon
} from '@mui/icons-material';

import useRoadmapStore from '../store/roadmapStore';
import { exportRoadmapToPDF } from '../utils/pdfExport';
import { STEP_LABELS } from '../utils/constants';
import PageContainer from '../components/PageContainer';
import ProjectSharing from '../components/ProjectSharing';
import AIGenerateModal from '../components/AIGenerateModal';
import apiService from '../utils/api';

const STEP_ICONS = {
  vision: <VisionIcon sx={{ fontSize: 15 }} />,
  situation_analysis: <AnalyticsIcon sx={{ fontSize: 15 }} />,
  strategic_objectives: <ObjectiveIcon sx={{ fontSize: 15 }} />,
  action_components: <TaskIcon sx={{ fontSize: 15 }} />,
  resources: <ResourceIcon sx={{ fontSize: 15 }} />,
  timeline: <TimelineIcon sx={{ fontSize: 15 }} />,
  risks: <ShieldIcon sx={{ fontSize: 15 }} />,
  governance: <GovernanceIcon sx={{ fontSize: 15 }} />,
  performance: <PerformanceIcon sx={{ fontSize: 15 }} />,
  adaptation: <AdaptationIcon sx={{ fontSize: 15 }} />
};

const STEP_ROUTES = {
  vision: '/vision',
  situation_analysis: '/situation',
  strategic_objectives: '/objectives',
  action_components: '/components',
  resources: '/resources',
  timeline: '/timeline',
  risks: '/risks',
  governance: '/governance',
  performance: '/performance',
  adaptation: '/adaptation'
};

export default function HomePage() {
  const {
    roadmapData,
    roadmaps,
    currentRoadmapId,
    updateRoadmapData,
    updateStepData,
    getProgress,
    isStepCompleted,
    loadRoadmaps,
    loadRoadmap,
    saveRoadmap,
    newProject,
    deleteRoadmap,
    isLoading,
    error
  } = useRoadmapStore();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();

  const [projectName, setProjectName] = useState(roadmapData.project_name);
  const [projectType, setProjectType] = useState(roadmapData.project_type);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [showJoinProjectDialog, setShowJoinProjectDialog] = useState(false);
  const [showConfirmNewDialog, setShowConfirmNewDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectType, setNewProjectType] = useState('personal');
  const [joinProjectId, setJoinProjectId] = useState('');
  const [joinError, setJoinError] = useState('');
  const [showAIGenerateModal, setShowAIGenerateModal] = useState(false);

  const progress = getProgress();

  useEffect(() => { loadRoadmaps(); }, [loadRoadmaps]);

  useEffect(() => {
    if (!isLoading && !currentRoadmapId && roadmaps.length === 0) {
      const hasShown = sessionStorage.getItem('hasShownNewProjectDialog');
      if (!hasShown) {
        setShowNewProjectDialog(true);
        sessionStorage.setItem('hasShownNewProjectDialog', 'true');
      }
    }
  }, [isLoading, currentRoadmapId, roadmaps.length]);

  useEffect(() => {
    setProjectName(roadmapData.project_name);
    setProjectType(roadmapData.project_type);
  }, [roadmapData.project_name, roadmapData.project_type]);

  const handleSaveProject = async () => {
    await saveRoadmap();
    setShowProjectDialog(false);
  };

  const handleLoadProject = async (roadmap) => {
    await loadRoadmap(roadmap._id);
    setShowLoadDialog(false);
  };

  const handleNewProject = () => {
    if (currentRoadmapId || (roadmapData.project_name && roadmapData.project_name !== 'My Roadmap Project')) {
      setShowConfirmNewDialog(true);
    } else {
      setNewProjectName('');
      setNewProjectType('personal');
      setShowNewProjectDialog(true);
    }
  };

  const handleConfirmNewProject = () => {
    setShowConfirmNewDialog(false);
    setNewProjectName('');
    setNewProjectType('personal');
    setShowNewProjectDialog(true);
  };

  const handleExportPDF = () => { exportRoadmapToPDF(roadmapData, t); };

  const handleAIGenerated = (aiData) => {
    newProject();
    const { project_name, ...steps } = aiData;
    updateRoadmapData({ project_name: project_name || 'AI Generated Roadmap', project_type: 'business' });
    Object.entries(steps).forEach(([stepName, stepData]) => {
      if (stepData && typeof stepData === 'object') {
        updateStepData(stepName, stepData);
      }
    });
  };

  const handleCreateNewProject = () => {
    if (newProjectName.trim()) {
      newProject();
      updateRoadmapData({ project_name: newProjectName, project_type: newProjectType });
      setShowNewProjectDialog(false);
    }
  };

  const handleJoinProject = async () => {
    if (joinProjectId.trim()) {
      try {
        setJoinError('');
        const response = await apiService.getRoadmapByProjectId(joinProjectId.trim().toLowerCase());
        const roadmap = response.data || response;
        if (roadmap && roadmap._id) {
          await loadRoadmap(roadmap._id);
          setShowJoinProjectDialog(false);
          setJoinProjectId('');
        }
      } catch (err) {
        setJoinError(err.message || 'Project not found');
      }
    }
  };

  const handleDeleteProject = async (id) => {
    if (window.confirm(t('common.confirmDelete'))) {
      await deleteRoadmap(id);
    }
  };

  const completedSteps = Object.keys(roadmapData.steps).filter(step => isStepCompleted(step)).length;
  const totalSteps = Object.keys(roadmapData.steps).length;

  const firstIncompleteStep = useMemo(() => {
    const entry = Object.keys(roadmapData.steps).find(step => !isStepCompleted(step));
    return entry || 'vision';
  }, [roadmapData.steps, isStepCompleted]);

  const focusRoute = STEP_ROUTES[firstIncompleteStep] || '/vision';

  const stepList = Object.entries(STEP_LABELS).map(([key, labelKey], idx) => ({
    key,
    label: t(labelKey),
    num: idx + 1,
    completed: isStepCompleted(key),
    route: STEP_ROUTES[key]
  }));

  const inlineStats = [
    { label: t('dashboard.stats.objectives'), value: roadmapData.steps.strategic_objectives?.objectives?.length || 0, color: '#6366f1' },
    { label: t('dashboard.stats.components'),  value: roadmapData.steps.action_components?.components?.length || 0, color: 'var(--status-sky)' },
    {
      label: t('dashboard.stats.resources'),
      value: [
        ...(roadmapData.steps.resources?.human_resources || []),
        ...(roadmapData.steps.resources?.financial_resources || []),
        ...(roadmapData.steps.resources?.technology_resources || []),
        ...(roadmapData.steps.resources?.other_resources || [])
      ].length,
      color: 'var(--status-green)'
    },
    { label: t('dashboard.stats.risks'),  value: roadmapData.steps.risks?.risk_items?.length || 0, color: 'var(--status-amber)' },
    { label: t('dashboard.stats.phases'), value: roadmapData.steps.timeline?.phases?.length || 0, color: 'var(--status-sky)' },
    { label: t('dashboard.stats.kpis'),   value: roadmapData.steps.performance?.kpis?.length || 0, color: 'var(--ai-accent)' }
  ];

  /* ────────────────────────── RENDER ────────────────────────────── */
  return (
    <PageContainer>

      {/* ── WELCOME STATE (no active project) ─────────────────────── */}
      {!currentRoadmapId && (
        <Box sx={{ maxWidth: 580, mx: 'auto', py: { xs: 2, md: 5 } }}>

          {/* Branding */}
          <Stack alignItems="center" spacing={1} sx={{ mb: 5 }}>
            <Box sx={{
              width: 52, height: 52, borderRadius: 2,
              bgcolor: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5
            }}>
              <FlagIcon sx={{ color: '#6366f1', fontSize: 26 }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--fg-strong)' }}>
              Roadmap Lab
            </Typography>
            <Typography sx={{ fontSize: '0.92rem', color: 'var(--fg-faint)', textAlign: 'center' }}>
              Plan, track, and execute your strategic roadmap
            </Typography>
          </Stack>

          {/* Existing projects list */}
          {roadmaps.length > 0 && (
            <Box sx={{
              bgcolor: 'background.paper',
              border: '1px solid var(--line-1)',
              borderRadius: 2, overflow: 'hidden', mb: 2
            }}>
              <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid var(--line-1)' }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--fg-faint)', textTransform: 'uppercase', letterSpacing: 1 }}>
                  Your Projects · {roadmaps.length}
                </Typography>
              </Box>
              {roadmaps.map((roadmap, i) => (
                <Box
                  key={roadmap._id}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 2,
                    px: 2.5, py: 1.75,
                    borderTop: i === 0 ? 'none' : '1px solid var(--line-1)',
                    cursor: 'pointer', transition: 'all 0.15s',
                    '&:hover': { bgcolor: 'rgba(99,102,241,0.05)' }
                  }}
                  onClick={() => handleLoadProject(roadmap)}
                >
                  <Box sx={{
                    width: 34, height: 34, borderRadius: 1.5, flexShrink: 0,
                    bgcolor: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {roadmap.project_type === 'business'
                      ? <BusinessIcon sx={{ color: '#6366f1', fontSize: 17 }} />
                      : <PersonIcon sx={{ color: '#6366f1', fontSize: 17 }} />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--fg-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {roadmap.project_name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'var(--fg-faint)' }}>
                      {t(`projectTypes.${roadmap.project_type}`)} · {roadmap.completion_status || 0}% complete
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ height: 4, width: 48, borderRadius: 10, bgcolor: 'var(--line-1)', overflow: 'hidden' }}>
                      <Box sx={{
                        height: '100%', borderRadius: 10,
                        width: `${roadmap.completion_status || 0}%`,
                        background: 'linear-gradient(90deg, #6366f1, #3b82f6)'
                      }} />
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleDeleteProject(roadmap._id); }}
                      sx={{ color: 'var(--line-soft)', '&:hover': { color: 'var(--status-red)' } }}
                    >
                      <DeleteIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Stack>
                </Box>
              ))}
            </Box>
          )}

          {/* Action cards */}
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <Box
                onClick={handleNewProject}
                sx={{
                  bgcolor: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.18)',
                  borderRadius: 2, p: 2.5, cursor: 'pointer', transition: 'all 0.15s',
                  '&:hover': { bgcolor: 'rgba(99,102,241,0.13)', borderColor: 'rgba(99,102,241,0.38)' }
                }}
              >
                <AddIcon sx={{ color: '#6366f1', fontSize: 24, mb: 1 }} />
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--fg-strong)', mb: 0.3 }}>
                  {t('actions.newProject')}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--fg-faint)' }}>
                  Start a new strategic roadmap
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box
                onClick={() => setShowAIGenerateModal(true)}
                sx={{
                  bgcolor: 'rgba(168,85,247,0.07)', border: '1px solid rgba(168,85,247,0.18)',
                  borderRadius: 2, p: 2.5, cursor: 'pointer', transition: 'all 0.15s',
                  background: 'linear-gradient(135deg, rgba(168,85,247,0.07), rgba(99,102,241,0.07))',
                  '&:hover': { bgcolor: 'rgba(168,85,247,0.13)', borderColor: 'rgba(168,85,247,0.38)' }
                }}
              >
                <AdaptationIcon sx={{ color: 'var(--ai-accent)', fontSize: 24, mb: 1 }} />
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--fg-strong)', mb: 0.3 }}>
                  {t('ai.generateNew')}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--fg-faint)' }}>
                  {t('ai.poweredByClaude')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box
                onClick={() => setShowJoinProjectDialog(true)}
                sx={{
                  bgcolor: 'background.paper', border: '1px solid var(--line-1)',
                  borderRadius: 2, p: 2.5, cursor: 'pointer', transition: 'all 0.15s',
                  '&:hover': { borderColor: 'rgba(99,102,241,0.2)', bgcolor: 'rgba(99,102,241,0.04)' }
                }}
              >
                <ShareIcon sx={{ color: 'var(--fg-faint)', fontSize: 24, mb: 1 }} />
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--fg-strong)', mb: 0.3 }}>
                  {t('actions.joinProject') || 'Join Project'}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--fg-faint)' }}>
                  Enter a Project ID to join
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* ── PROJECT VIEW ──────────────────────────────────────────── */}
      {currentRoadmapId && (
        <>
          {/* ── Header card ── */}
          <Box sx={{
            bgcolor: 'background.paper',
            border: '1px solid var(--line-1)',
            borderRadius: 2,
            p: { xs: 2.5, md: 3 },
            position: 'relative', overflow: 'hidden',
            '&::before': {
              content: '""', position: 'absolute',
              left: 0, top: 0, bottom: 0, width: 3,
              background: progress === 100
                ? 'linear-gradient(180deg,#22c55e,#16a34a)'
                : 'linear-gradient(180deg,#6366f1,#3b82f6)'
            }
          }}>
            <Grid container spacing={2} alignItems="center">
              {/* Left: title */}
              <Grid item xs={12} md={7} sx={{ pl: '20px !important' }}>
                <Chip
                  icon={roadmapData.project_type === 'business'
                    ? <BusinessIcon sx={{ fontSize: '13px !important' }} />
                    : <PersonIcon sx={{ fontSize: '13px !important' }} />}
                  label={t(`projectTypes.${roadmapData.project_type}`)}
                  size="small"
                  sx={{ height: 20, fontSize: '0.68rem', bgcolor: 'rgba(99,102,241,0.1)', color: 'var(--accent-fg)', border: '1px solid rgba(99,102,241,0.22)', mb: 1 }}
                />
                <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--fg-strong)', lineHeight: 1.2, mb: 0.5 }}>
                  {roadmapData.project_name || t('app.title')}
                </Typography>
                {roadmapData.created_date && (
                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--fg-faint)' }}>
                    Created {roadmapData.created_date}
                  </Typography>
                )}
              </Grid>

              {/* Right: action buttons */}
              <Grid item xs={12} md={5}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                  <Button
                    variant="contained" size="small"
                    endIcon={<LaunchIcon sx={{ fontSize: '14px !important' }} />}
                    href={focusRoute}
                    sx={{
                      fontSize: '0.78rem', px: 2, py: 0.8,
                      background: 'linear-gradient(135deg,#6366f1,#3b82f6)',
                      boxShadow: 'none', '&:hover': { boxShadow: 'none' }
                    }}
                  >
                    {t('actions.continueJourney')}
                  </Button>
                  <Button
                    variant="outlined" size="small"
                    startIcon={<PdfIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={handleExportPDF}
                    sx={{ fontSize: '0.78rem', px: 1.5, py: 0.8, color: 'var(--status-green)', borderColor: 'rgba(74,222,128,0.25)', '&:hover': { borderColor: '#4ade80', bgcolor: 'rgba(74,222,128,0.05)' } }}
                  >
                    PDF
                  </Button>
                  <Button
                    variant="outlined" size="small"
                    startIcon={<SaveIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={() => setShowProjectDialog(true)}
                    sx={{ fontSize: '0.78rem', px: 1.5, py: 0.8, borderColor: 'var(--line-2)', color: 'var(--fg-faint)', '&:hover': { borderColor: 'rgba(255,255,255,0.22)' } }}
                  >
                    {t('common.save')}
                  </Button>
                  <Button
                    variant="outlined" size="small"
                    startIcon={<AddIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={handleNewProject}
                    sx={{ fontSize: '0.78rem', px: 1.5, py: 0.8, borderColor: 'var(--line-2)', color: 'var(--fg-faint)', '&:hover': { borderColor: 'rgba(255,255,255,0.22)' } }}
                  >
                    New
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            {/* Progress */}
            <Box sx={{ pl: '20px', mt: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'var(--fg-faint)', fontWeight: 600 }}>
                  {completedSteps} of {totalSteps} steps completed
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'var(--fg-faint)', fontWeight: 700 }}>
                  {progress}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate" value={progress}
                sx={{
                  height: 4, borderRadius: 10, bgcolor: 'var(--line-1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 10,
                    background: progress === 100
                      ? 'linear-gradient(90deg,#22c55e,#16a34a)'
                      : 'linear-gradient(90deg,#6366f1,#3b82f6)'
                  }
                }}
              />
            </Box>
          </Box>

          {/* ── Stats row ── */}
          <Grid container spacing={1.5}>
            {inlineStats.map((stat) => (
              <Grid item xs={4} sm={2} key={stat.label}>
                <Box sx={{
                  bgcolor: 'background.paper', border: '1px solid var(--line-1)',
                  borderRadius: 2, p: { xs: 1.5, sm: 2 }, textAlign: 'center',
                  transition: 'border-color 0.15s', '&:hover': { borderColor: stat.color + '40' }
                }}>
                  <Typography sx={{ fontSize: { xs: '1.25rem', sm: '1.55rem' }, fontWeight: 800, color: stat.color, lineHeight: 1, mb: 0.5 }}>
                    {stat.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: 'var(--fg-faint)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* ── Steps + Right column ── */}
          <Grid container spacing={2}>

            {/* Steps list */}
            <Grid item xs={12} md={7}>
              <Box sx={{ bgcolor: 'background.paper', border: '1px solid var(--line-1)', borderRadius: 2, p: 2.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--fg-faint)', textTransform: 'uppercase', letterSpacing: 1 }}>
                    {t('dashboard.roadmapSteps')}
                  </Typography>
                  <Chip
                    label={`${completedSteps}/${totalSteps}`} size="small"
                    sx={{ height: 20, fontSize: '0.68rem', bgcolor: 'rgba(34,197,94,0.08)', color: 'var(--status-green)', border: '1px solid rgba(34,197,94,0.18)' }}
                  />
                </Stack>
                <Stack spacing={0.25}>
                  {stepList.map((step) => (
                    <Box
                      key={step.key}
                      onClick={() => navigate(step.route)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        px: 1.5, py: 0.9, borderRadius: 1.5, cursor: 'pointer',
                        transition: 'all 0.15s',
                        '&:hover': { bgcolor: 'rgba(99,102,241,0.06)', '& .step-arrow': { opacity: 1 } }
                      }}
                    >
                      <Box sx={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: step.completed ? 'rgba(34,197,94,0.1)' : 'var(--line-1)',
                        border: '1px solid ' + (step.completed ? 'rgba(34,197,94,0.28)' : 'var(--line-2)'),
                        color: step.completed ? 'var(--status-green)' : 'var(--line-soft)'
                      }}>
                        {step.completed
                          ? <CheckIcon sx={{ fontSize: 13 }} />
                          : <Typography sx={{ fontSize: '0.6rem', fontWeight: 700 }}>{step.num}</Typography>}
                      </Box>
                      <Box sx={{ color: step.completed ? 'var(--status-green)' : 'var(--line-soft)', flexShrink: 0 }}>
                        {STEP_ICONS[step.key]}
                      </Box>
                      <Typography sx={{
                        fontSize: '0.84rem', flex: 1, fontWeight: step.completed ? 500 : 400,
                        color: step.completed ? 'var(--fg-strong)' : 'var(--fg-faint)'
                      }}>
                        {step.label}
                      </Typography>
                      <ArrowIcon className="step-arrow" sx={{ fontSize: 13, color: 'var(--line-soft)', opacity: 0, transition: 'opacity 0.15s', flexShrink: 0 }} />
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>

            {/* Right column */}
            <Grid item xs={12} md={5}>
              <Stack spacing={2}>

                {/* Project info + quick rename */}
                <Box sx={{ bgcolor: 'background.paper', border: '1px solid var(--line-1)', borderRadius: 2, p: 2.5 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--fg-faint)', textTransform: 'uppercase', letterSpacing: 1, mb: 1.75 }}>
                    {t('dashboard.projectInfo')}
                  </Typography>
                  <Stack spacing={1.25} sx={{ mb: 2 }}>
                    {[
                      { label: t('dashboard.projectName'), value: roadmapData.project_name },
                      { label: t('dashboard.type'), value: t(`projectTypes.${roadmapData.project_type}`) },
                      { label: t('dashboard.created'), value: roadmapData.created_date },
                      { label: t('dashboard.lastModified'), value: roadmapData.last_modified }
                    ].filter(r => r.value).map(row => (
                      <Stack key={row.label} direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography sx={{ fontSize: '0.73rem', color: 'var(--fg-faint)', flexShrink: 0 }}>
                          {row.label}
                        </Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: 'var(--fg-muted)', fontWeight: 500, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>
                          {row.value}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>

                  {/* Quick rename/retype */}
                  <Box sx={{ pt: 1.75, borderTop: '1px solid var(--line-1)' }}>
                    <TextField
                      fullWidth size="small"
                      label={t('home.projectName')}
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      onBlur={() => {
                        if (projectName.trim()) {
                          updateRoadmapData({ project_name: projectName, project_type: projectType });
                        }
                      }}
                      sx={{ mb: 1.25 }}
                    />
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('home.projectType')}</InputLabel>
                      <Select
                        value={projectType}
                        onChange={(e) => {
                          setProjectType(e.target.value);
                          updateRoadmapData({ project_name: projectName, project_type: e.target.value });
                        }}
                        label={t('home.projectType')}
                      >
                        <MenuItem value="personal">{t('home.personalGoals')}</MenuItem>
                        <MenuItem value="business">{t('home.businessInitiative')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                {/* Sharing */}
                <Box sx={{ bgcolor: 'background.paper', border: '1px solid var(--line-1)', borderRadius: 2, p: 2.5 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--fg-faint)', textTransform: 'uppercase', letterSpacing: 1, mb: 1.75 }}>
                    {t('common.share')}
                  </Typography>
                  <ProjectSharing />
                </Box>

                {/* Switch project (only shown when user has multiple) */}
                {roadmaps.length > 1 && (
                  <Box sx={{ bgcolor: 'background.paper', border: '1px solid var(--line-1)', borderRadius: 2, overflow: 'hidden' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 2, borderBottom: '1px solid var(--line-1)' }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--fg-faint)', textTransform: 'uppercase', letterSpacing: 1 }}>
                        Switch Project
                      </Typography>
                      <Chip label={roadmaps.length} size="small"
                        sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'var(--line-1)', color: 'var(--fg-faint)' }} />
                    </Stack>
                    {roadmaps.filter(r => r._id !== currentRoadmapId).slice(0, 4).map((roadmap, i) => (
                      <Box
                        key={roadmap._id}
                        onClick={() => handleLoadProject(roadmap)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5,
                          px: 2.5, py: 1.5,
                          borderTop: i === 0 ? 'none' : '1px solid var(--line-1)',
                          cursor: 'pointer', transition: 'all 0.15s',
                          '&:hover': { bgcolor: 'rgba(99,102,241,0.05)' }
                        }}
                      >
                        <Box sx={{
                          width: 28, height: 28, borderRadius: 1, flexShrink: 0,
                          bgcolor: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.15)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          {roadmap.project_type === 'business'
                            ? <BusinessIcon sx={{ color: '#6366f1', fontSize: 14 }} />
                            : <PersonIcon sx={{ color: '#6366f1', fontSize: 14 }} />}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--fg-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {roadmap.project_name}
                          </Typography>
                          <Typography sx={{ fontSize: '0.68rem', color: 'var(--fg-faint)' }}>
                            {roadmap.completion_status || 0}% complete
                          </Typography>
                        </Box>
                        <ArrowIcon sx={{ fontSize: 13, color: 'var(--line-soft)', flexShrink: 0 }} />
                      </Box>
                    ))}
                  </Box>
                )}

              </Stack>
            </Grid>
          </Grid>
        </>
      )}

      {/* ─────────────────── DIALOGS ─────────────────────────────── */}

      {/* New Project */}
      <Dialog open={showNewProjectDialog} onClose={() => setShowNewProjectDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AddIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>{t('actions.newProject')}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
              {t('home.projectConfigurationHint') || 'Give your project a name and choose the type that best fits your goals.'}
            </Alert>
            <TextField
              fullWidth label={t('home.projectName')}
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              placeholder={newProjectType === 'personal' ? 'e.g., Career Development 2026' : 'e.g., Market Expansion Strategy'}
              sx={{ mb: 3 }} autoFocus
            />
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>{t('home.projectType')}</Typography>
            <RadioGroup value={newProjectType} onChange={(e) => setNewProjectType(e.target.value)}>
              {[
                { value: 'personal', icon: <PersonIcon color="primary" />, label: t('home.personalGoals') || 'Personal Goals', desc: t('home.personalGoalsDesc') || 'Career goals, personal development, life projects' },
                { value: 'business', icon: <BusinessIcon color="primary" />, label: t('home.businessInitiative') || 'Business Initiative', desc: t('home.businessInitiativeDesc') || 'Business strategy, product launches, market expansions' }
              ].map(opt => (
                <Paper key={opt.value} variant="outlined"
                  sx={{ p: 2, mb: 1.5, cursor: 'pointer', border: 2, transition: 'all 0.2s',
                    borderColor: newProjectType === opt.value ? 'primary.main' : 'divider',
                    bgcolor: newProjectType === opt.value ? alpha(theme.palette.primary.main, 0.05) : 'transparent' }}
                  onClick={() => setNewProjectType(opt.value)}>
                  <FormControlLabel value={opt.value} control={<Radio />}
                    label={
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">{opt.icon}
                          <Typography variant="body1" fontWeight={600}>{opt.label}</Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{opt.desc}</Typography>
                      </Box>
                    }
                    sx={{ m: 0, width: '100%' }} />
                </Paper>
              ))}
            </RadioGroup>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setShowNewProjectDialog(false)} size="large">{t('common.cancel')}</Button>
          <Button onClick={handleCreateNewProject} variant="contained" disabled={!newProjectName.trim()} size="large" startIcon={<LaunchIcon />}>
            {t('actions.start') || 'Create Project'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Join Project */}
      <Dialog open={showJoinProjectDialog} onClose={() => { setShowJoinProjectDialog(false); setJoinError(''); setJoinProjectId(''); }} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ShareIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>{t('actions.joinProject') || 'Join Project'}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
              {t('home.joinProjectHint') || 'Enter the 8-character Project ID shared by the project owner.'}
            </Alert>
            {joinError && <Alert severity="error" sx={{ mb: 3 }}>{joinError}</Alert>}
            <TextField
              fullWidth label={t('home.projectId') || 'Project ID'}
              value={joinProjectId}
              onChange={(e) => setJoinProjectId(e.target.value.toUpperCase())}
              placeholder="A3F5B2C1" sx={{ mb: 2 }} autoFocus
              inputProps={{ style: { textTransform: 'uppercase', fontFamily: 'monospace', letterSpacing: 3, fontSize: '1.2rem', textAlign: 'center' }, maxLength: 8 }}
              helperText={`${joinProjectId.length}/8 characters`}
            />
            <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), border: 1, borderColor: 'divider' }}>
              <Stack spacing={0.75}>
                <Typography variant="caption" fontWeight={600} color="primary">How to find a Project ID:</Typography>
                <Typography variant="caption" color="text.secondary">• Ask the project owner to share their Project ID</Typography>
                <Typography variant="caption" color="text.secondary">• Owner can find it in: Project Settings → Sharing</Typography>
              </Stack>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => { setShowJoinProjectDialog(false); setJoinError(''); setJoinProjectId(''); }} size="large">{t('common.cancel')}</Button>
          <Button onClick={handleJoinProject} variant="contained" disabled={!joinProjectId.trim() || joinProjectId.trim().length !== 8} size="large" startIcon={<LaunchIcon />}>
            {t('actions.joinProject') || 'Join Project'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Project */}
      <Dialog open={showProjectDialog} onClose={() => setShowProjectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('actions.saveProject')}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField fullWidth label={t('common.name')} value={projectName} onChange={(e) => setProjectName(e.target.value)} sx={{ mt: 1, mb: 2 }} />
          <RadioGroup value={projectType} onChange={(e) => setProjectType(e.target.value)}>
            <FormControlLabel value="personal" control={<Radio />} label={t('projectTypes.personal')} />
            <FormControlLabel value="business" control={<Radio />} label={t('projectTypes.business')} />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowProjectDialog(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSaveProject} variant="contained" disabled={!projectName.trim()}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Load Project */}
      <Dialog open={showLoadDialog} onClose={() => setShowLoadDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('actions.loadProject')}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <List>
            {roadmaps.map((roadmap) => (
              <ListItem key={roadmap._id}>
                <ListItemText
                  primary={roadmap.project_name}
                  secondary={`${roadmap.project_type} · ${roadmap.completion_status || 0}% · ${roadmap.last_modified}`}
                />
                <ListItemSecondaryAction>
                  <IconButton onClick={() => handleLoadProject(roadmap)}><LoadIcon /></IconButton>
                  <IconButton onClick={() => handleDeleteProject(roadmap._id)}><DeleteIcon /></IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
          {roadmaps.length === 0 && (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>{t('messages.noProjects')}</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setShowLoadDialog(false)} size="large">{t('common.close') || 'Close'}</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm New Project */}
      <Dialog open={showConfirmNewDialog} onClose={() => setShowConfirmNewDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningIcon color="warning" />
            <Typography variant="h6" fontWeight={600}>{t('common.confirm') || 'Confirm'}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" gutterBottom>{t('messages.confirmNewProject') || 'Start a new project?'}</Typography>
            <Typography variant="body2" color="text.secondary">{t('messages.unsavedWarning') || 'Unsaved changes will be lost.'}</Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setShowConfirmNewDialog(false)} size="large">{t('common.cancel')}</Button>
          <Button onClick={handleConfirmNewProject} variant="contained" color="warning" size="large">{t('common.continue') || 'Continue'}</Button>
        </DialogActions>
      </Dialog>

      {/* AI Generate Modal */}
      <AIGenerateModal
        open={showAIGenerateModal}
        onClose={() => setShowAIGenerateModal(false)}
        onGenerated={handleAIGenerated}
      />

    </PageContainer>
  );
}
